import { ICameraCCTV } from '@api/models';
import { updateCctvCamera } from '@api/services/cctv-camera.service';
import { useMutation } from '@tanstack/react-query';
import moment from 'moment';
import * as Yup from 'yup';

interface EditCctvCameraFormOptions {
    cctvCamera: ICameraCCTV;
    clientId: string;
    projectId: string;
}

const useCctvCameraUpdateForm = (options: EditCctvCameraFormOptions) => {
    const { cctvCamera, clientId, projectId } = options;

    const updateCameraMutation = useMutation((payload: any) =>
        updateCctvCamera(clientId, projectId, cctvCamera._id, payload)
    );

    const schema = Yup.object().shape({
        name: Yup.string().trim().required('This field is required'),
        installationDate: Yup.string().trim().required('This field is required'),
        qLiveUnit: Yup.string().trim().required('This field is required'),
        storage: Yup.string().required('This field is required'),
        status: Yup.string().nullable(),
        isPrimary: Yup.boolean().required(),
        timezone: Yup.string().trim(),

        // ✅ map fields
        angle: Yup.number().nullable().min(0).max(360),
        distance: Yup.number().nullable().min(0).max(10000),
        fieldOfView: Yup.number().nullable().min(0).max(180),

        location: Yup.object().shape({
            latitude: Yup.string().trim(),
            longitude: Yup.string().trim(),
        }),

        channels: Yup.array()
            .of(
                Yup.object({
                    number: Yup.string().trim().required('Channel number is required'),
                    folder: Yup.string().trim().nullable(),
                    hlsUrl: Yup.string().trim().nullable(),
                    webrtcUrl: Yup.string().trim().nullable(),
                    ptzControl: Yup.object({
                        isAvailable: Yup.boolean().required('This field is required'),
                        deviceId: Yup.string()
                            .trim()
                            .when('isAvailable', {
                                is: true,
                                then: (s) => s.required('Device ID is required when PTZ is available'),
                                otherwise: (s) => s.notRequired(),
                            }),
                    }),
                })
            )
            .min(1, 'Add at least one channel'),
    });

    const initialValues = {
        name: cctvCamera?.name || '',
        installationDate: cctvCamera?.installationDate ? moment(cctvCamera.installationDate, 'YYYYMMDD') : moment(),
        qLiveUnit: cctvCamera?.qLiveUnit || '',
        storage: cctvCamera?.storageName || '',
        status: cctvCamera?.status || null,
        isPrimary: !!cctvCamera?.isPrimary,

        timezone: cctvCamera?.timezone,

        defaultChannelNumber: cctvCamera?.defaultChannel?.number || '',

        // ✅ map fields
        angle: Number(cctvCamera?.angle ?? 0),
        distance: Number(cctvCamera?.distance ?? 0),
        fieldOfView: Number(cctvCamera?.fieldOfView ?? 0),

        location: {
            latitude: cctvCamera?.location?.latitude || '',
            longitude: cctvCamera?.location?.longitude || '',
        },

        channels: cctvCamera?.channels?.map((ch) => ({
            _id: ch?._id,
            number: ch?.number ?? '',
            folder: ch?.folder ?? '',
            hlsUrl: ch?.hlsUrl ?? '',
            webrtcUrl: ch?.webrtcUrl ?? '',
            ptzControl: {
                isAvailable: !!ch?.ptzControl?.isAvailable,
                deviceId: ch?.ptzControl?.deviceId ?? '',
            },
        })) ?? [
            {
                number: '',
                folder: '',
                hlsUrl: '',
                webrtcUrl: '',
                ptzControl: { isAvailable: false, deviceId: '' },
            },
        ],
    };

    const submit = async (values: any) => {
        const payload = {
            ...values,
            installationDate: moment(values.installationDate).format('YYYYMMDD'),
            angle: values.angle === '' || values.angle == null ? null : Number(values.angle),
            distance: values.distance === '' || values.distance == null ? null : Number(values.distance),
            fieldOfView: values.fieldOfView === '' || values.fieldOfView == null ? null : Number(values.fieldOfView),
        };

        const data = await updateCameraMutation.mutateAsync(payload);
        return data;
    };

    return {
        schema,
        initialValues,
        submit,
        isSubmitting: updateCameraMutation.isLoading,
    };
};

export default useCctvCameraUpdateForm;
